import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { connectivity } from '../lib/connectivity';
import {
  clearOrgScopedWorkingState,
  getActiveOrgId,
  getUnsafeSwitchState,
  listMyOrgs,
  setActiveOrgId,
  type OrgMembership,
} from '../lib/org';
import {
  drain,
  getQueueSummary,
  recoverStaleUploading,
  retryFailedItems,
  type QueueSummary,
} from '../lib/syncQueue';

export function AppLayout() {
  const [online, setOnline] = useState(connectivity.isOnline());
  const [orgs, setOrgs] = useState<OrgMembership[]>([]);
  const [orgErr, setOrgErr] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);
  const [syncSummary, setSyncSummary] = useState<QueueSummary | null>(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    return connectivity.onStatusChange(setOnline);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        await recoverStaleUploading();
        const summary = await getQueueSummary();
        if (!cancelled) setSyncSummary(summary);
      } catch {
        if (!cancelled) setSyncSummary(null);
      }
    };
    void refresh();
    const onChange = () => void refresh();
    const interval = window.setInterval(() => void refresh(), 10_000);
    window.addEventListener('eva-field:sync-queue-changed', onChange);
    window.addEventListener('eva-field:sync-failed', onChange);
    window.addEventListener('eva-field:sync-confirmed', onChange);
    window.addEventListener('visibilitychange', onChange);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('eva-field:sync-queue-changed', onChange);
      window.removeEventListener('eva-field:sync-failed', onChange);
      window.removeEventListener('eva-field:sync-confirmed', onChange);
      window.removeEventListener('visibilitychange', onChange);
    };
  }, []);

  async function retrySync() {
    if (retrying) return;
    setRetrying(true);
    try {
      await recoverStaleUploading(0);
      await retryFailedItems();
      await drain();
      setSyncSummary(await getQueueSummary());
    } finally {
      setRetrying(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listMyOrgs();
        if (cancelled) return;
        setOrgs(rows);
        const existing = getActiveOrgId();
        if (!existing) {
          const current = rows.find((row) => row.is_current) ?? rows[0];
          if (current) setActiveOrgId(current.org_id);
        }
      } catch (e) {
        if (!cancelled) setOrgErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const dateLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const activeOrgId = getActiveOrgId();
  const currentOrg = orgs.find((row) => row.org_id === activeOrgId) ?? orgs.find((row) => row.is_current) ?? orgs[0];
  const hasMultipleOrgs = orgs.length > 1;

  async function onOrgChange(nextOrgId: string) {
    if (!nextOrgId || switching) return;
    if (nextOrgId === activeOrgId) return;
    setSwitching(true);
    try {
      const unsafe = await getUnsafeSwitchState();
      if (unsafe.hasUnsyncedItems) {
        const ok = window.confirm(
          `You have ${unsafe.unsyncedCount} unsynced photo item(s) across ${unsafe.affectedJobCount} job(s). Switch org anyway?`,
        );
        if (!ok) return;
      }
      setActiveOrgId(nextOrgId);
      clearOrgScopedWorkingState();
      window.location.assign('/');
    } finally {
      setSwitching(false);
    }
  }

  return (
    <div className="min-h-screen bg-white text-black">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-700">{dateLabel}</div>
          <div className="mt-0.5 flex items-center gap-2">
            <span className="rounded bg-eva-teal/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-eva-teal">
              Org
            </span>
            {hasMultipleOrgs ? (
              <select
                aria-label="Active organization"
                value={currentOrg?.org_id ?? ''}
                onChange={(e) => void onOrgChange(e.target.value)}
                disabled={switching}
                className="max-w-[180px] truncate rounded border border-gray-300 bg-white px-2 py-0.5 text-xs font-semibold"
              >
                {orgs.map((org) => (
                  <option key={org.org_id} value={org.org_id}>
                    {org.org_name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="max-w-[180px] truncate text-xs font-semibold text-gray-700">
                {currentOrg?.org_name ?? (orgErr ? 'Org unavailable' : 'Loading…')}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                online ? 'bg-eva-teal' : 'bg-bracket-amber'
              }`}
              aria-hidden
            />
            <span className="opacity-70">{online ? 'online' : 'offline'}</span>
          </div>
          <Link
            to="/settings"
            aria-label="Settings"
            className="rounded-full bg-gray-100 px-2 py-1 text-gray-700"
          >
            ⚙
          </Link>
        </div>
      </header>
      <SyncBanner
        online={online}
        summary={syncSummary}
        retrying={retrying}
        onRetry={() => void retrySync()}
      />
      <Outlet />
    </div>
  );
}

function SyncBanner({
  online,
  summary,
  retrying,
  onRetry,
}: {
  online: boolean;
  summary: QueueSummary | null;
  retrying: boolean;
  onRetry: () => void;
}) {
  if (!summary) return null;
  const waiting = summary.pending + summary.uploading;
  if (summary.failed === 0 && waiting === 0) return null;
  const tone =
    summary.failed > 0
      ? 'border-red-200 bg-red-50 text-red-800'
      : online
        ? 'border-bracket-amber/30 bg-bracket-amber/10 text-amber-900'
        : 'border-bracket-amber/40 bg-bracket-amber/10 text-amber-900';
  const message =
    summary.failed > 0
      ? `${summary.failed} failed photo upload${summary.failed === 1 ? '' : 's'}`
      : online
        ? `${waiting} photo upload${waiting === 1 ? '' : 's'} syncing`
        : `${waiting} photo upload${waiting === 1 ? '' : 's'} queued offline`;

  return (
    <div className={`sticky top-[57px] z-20 border-b px-4 py-2 text-xs ${tone}`}>
      <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-bold">{message}</div>
          {summary.affectedJobCount > 0 && (
            <div className="mt-0.5 opacity-75">
              Across {summary.affectedJobCount} job{summary.affectedJobCount === 1 ? '' : 's'}
            </div>
          )}
        </div>
        {(summary.failed > 0 || summary.uploading > 0) && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="shrink-0 rounded-lg border border-current/20 bg-white/70 px-3 py-1.5 font-bold disabled:opacity-50"
          >
            {retrying ? 'Retrying...' : 'Retry'}
          </button>
        )}
      </div>
    </div>
  );
}
